import React from "react";

import {
  compareRoleNames,
  getRoleDefinition,
  getRoleDisplayName,
} from "src/adminPanel/shared/roleCatalog";
import {
  describeRoleReach,
  RoleReach,
} from "src/adminPanel/users/assumableRoleUtils";
import { Checkbox } from "src/base-components/Checkbox";
import { Pill } from "src/base-components/Pill";
import { AssumableRole } from "src/clients/taktile-api";
import { Accordion } from "src/design-system/Accordion";
import { Tooltip } from "src/design-system/Tooltip";
import { pluralize } from "src/utils/stringUtils";

type Props = {
  role: AssumableRole;
  reach: RoleReach;
  isSelected: boolean;
  isDisabled: boolean;
  onToggle: () => void;
};

const DetailLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="font-inter-medium-11px text-gray-500">{children}</p>
);

const DetailLine: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <li className="font-inter-normal-12px text-gray-600">{children}</li>
);

export const AssumableRoleRow: React.FC<Props> = ({
  role,
  reach,
  isSelected,
  isDisabled,
  onToggle,
}) => {
  // A role can be reachable through more than one held role: ws_viewer via a
  // directly held ws_viewer on one workspace and a ws_editor on another.
  const viaRoles = Array.from(
    new Set(
      role.scopes.map((scope) => scope.via).filter((via) => via !== role.name),
    ),
  ).sort(compareRoleNames);

  return (
    <Accordion.Item
      dataLoc={`token-impersonation-role-${role.name}`}
      description={
        <span className="flex items-center gap-1">
          <span className="shrink-0 font-code-12">{role.name}</span>
          <span aria-hidden="true">·</span>
          <span className="min-w-0 truncate">{describeRoleReach(reach)}</span>
        </span>
      }
      label={getRoleDisplayName(role.name)}
      rightSlot={
        <div className="flex items-center gap-2">
          {viaRoles.length > 0 && (
            <Tooltip
              body={
                <div className="flex flex-col gap-0.5">
                  {viaRoles.map((name) => (
                    <span key={name}>{getRoleDisplayName(name)}</span>
                  ))}
                </div>
              }
              disabled={viaRoles.length === 1}
              placement="top"
              asChild
            >
              <span className="shrink-0">
                <Pill variant="gray">
                  <Pill.Text>
                    {viaRoles.length === 1
                      ? `via ${getRoleDisplayName(viaRoles[0])}`
                      : `via ${pluralize(viaRoles.length, "role")}`}
                  </Pill.Text>
                </Pill>
              </span>
            </Tooltip>
          )}
          <Checkbox
            aria-label={`Select ${getRoleDisplayName(role.name)}`}
            checked={isSelected}
            dataLoc={`token-impersonation-checkbox-${role.name}`}
            disabled={isDisabled}
            onChange={onToggle}
          />
        </div>
      }
      value={role.name}
    >
      <div
        className="flex flex-col gap-3"
        data-loc={`token-impersonation-detail-${role.name}`}
      >
        {(reach.allOrganizations || reach.organizationNames.length > 0) && (
          <div>
            <DetailLabel>Organizations</DetailLabel>
            <ul className="mt-1 max-h-64 overflow-y-auto">
              {reach.allOrganizations && (
                <DetailLine>Every organization.</DetailLine>
              )}
              {reach.organizationNames.map((name) => (
                <DetailLine key={name}>{name}</DetailLine>
              ))}
            </ul>
          </div>
        )}
        {(reach.allWorkspaces ||
          reach.everyWorkspaceIn.length > 0 ||
          reach.workspaces.length > 0) && (
          <div>
            <DetailLabel>Workspaces</DetailLabel>
            <ul className="mt-1 max-h-64 overflow-y-auto">
              {reach.allWorkspaces && (
                <DetailLine>Every workspace in every organization.</DetailLine>
              )}
              {reach.everyWorkspaceIn.map((name) => (
                <DetailLine key={name}>{name} / every workspace</DetailLine>
              ))}
              {reach.workspaces.map((workspace) => (
                <DetailLine key={workspace.id}>
                  {workspace.organizationName} / {workspace.name}
                  {workspace.via !== role.name && (
                    <span className="text-gray-500">
                      {" "}
                      (via {getRoleDisplayName(workspace.via)})
                    </span>
                  )}
                </DetailLine>
              ))}
            </ul>
          </div>
        )}
        <div>
          <DetailLabel>Included sub-roles</DetailLabel>
          {role.included_sub_roles.length === 0 ? (
            <ul className="mt-1">
              <DetailLine>None — this role has no sub-roles.</DetailLine>
            </ul>
          ) : (
            <div className="mt-1 flex flex-wrap gap-1.5">
              {[...role.included_sub_roles]
                .sort(compareRoleNames)
                .map((name) => (
                  <Pill
                    key={name}
                    variant={getRoleDefinition(name) ? "gray" : "outlined-gray"}
                  >
                    <Pill.Text>{getRoleDisplayName(name)}</Pill.Text>
                  </Pill>
                ))}
            </div>
          )}
        </div>
      </div>
    </Accordion.Item>
  );
};
